import { useQuery } from '@tanstack/react-query';
import { api } from './api-client';
import type { EmployeeDto, EmployeeSearchResultDto, EmployeeSummaryDto } from './types';

// One employee's profile as the effective viewer may read it (the server redacts per viewer). It is
// the profile page's own read and cache entry, so starting or stopping View as, which invalidates
// every ['employee'] key, re-resolves it for the new identity. A 403 or 404 is a stable answer.
export function useEmployee(id: string) {
  return useQuery({
    queryKey: ['employee', id],
    queryFn: () => api.get<EmployeeDto>(`/v1/employees/${id}`),
    retry: false,
  });
}

export function useEmployees() {
  return useQuery({
    queryKey: ['employees'],
    queryFn: () => api.get<EmployeeSummaryDto[]>('/v1/employees'),
  });
}

// Minimum characters before either the client or the server answers a global search.
export const MIN_SEARCH_CHARS = 2;

// Typeahead behind the global search bar. Deliberately its own endpoint and cache key: this is the
// one search that includes former employees, while the ['employees'] directory list that every
// picker in the app shares must keep excluding them. Idle below the character floor, so the bar
// never asks for a whole-roster match on a single letter. Pass an already-debounced query.
export function useEmployeeSearch(query: string) {
  const term = query.trim();
  return useQuery({
    queryKey: ['employees', 'global-search', term],
    queryFn: () =>
      api.get<EmployeeSearchResultDto[]>(`/v1/employees/search-all?q=${encodeURIComponent(term)}`),
    enabled: term.length >= MIN_SEARCH_CHARS,
    staleTime: 30_000,
  });
}

// Manager of the given employee, resolved server-side so the sidebar card works independently of
// what the /v1/employees directory list happens to contain (that list is company-wide now).
// A 204 (no supervisor) arrives as undefined from the api client; normalize to null so TanStack
// Query gets a non-undefined value. The `.id` guard also treats an empty-object response as null.
export function useManager(employeeId: number, enabled: boolean) {
  return useQuery<EmployeeSummaryDto | null>({
    queryKey: ['employee', employeeId, 'manager'],
    queryFn: async () => {
      const result = await api.get<EmployeeSummaryDto | undefined>(
        `/v1/employees/${employeeId}/manager`,
      );
      return result && result.id ? result : null;
    },
    enabled,
  });
}
